import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import {
  Input,
  Text,
  TextArea,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { TIMEKEEPING_REASON_CONSTANTS } from "@src/appV2/redesign/WorkerShifts/Timekeeping/constants";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const SHIFT_EXTENSION_REASONS = [
  "My relief is late",
  "There was an emergency or incident that prolonged my shift",
  "I need to finish charting/assignments",
  "I had to pass on information to the next shift",
  "Other",
] as const;

const extendShiftFormBaseSchema = z.object({
  supervisorName: z.string().trim().min(1, "Supervisor name is required"),
  reason: z.string().min(1, "Select a reason"),
  additionalDetails: z
    .string()
    .max(200, "Additional details must be 200 characters or fewer")
    .optional(),
});

type ExtendShiftFormValues = z.infer<typeof extendShiftFormBaseSchema>;

export function formatShiftExtensionReason(values: ExtendShiftFormValues): string {
  const parts = [
    `Supervisor name: ${values.supervisorName}`,
    `Reason for extending shift: ${values.reason}`,
  ];

  if (isDefined(values.additionalDetails) && values.additionalDetails.trim().length > 0) {
    parts.push(`Additional details: ${values.additionalDetails.trim()}`);
  } else {
    parts.push("Additional details: None provided");
  }

  return parts.join("\n");
}

const extendShiftFormSchema = extendShiftFormBaseSchema.refine(
  (values) =>
    formatShiftExtensionReason(values).length <= TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS,
  {
    message: `Combined form content exceeds the ${TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS} character limit`,
    path: ["additionalDetails"],
  }
);

interface ExtendShiftBottomSheetProps {
  modalState: UseModalState;
  isSubmitting: boolean;
  onSubmit: (reason: string) => void;
}

export function ExtendShiftBottomSheet(props: ExtendShiftBottomSheetProps) {
  const { modalState, isSubmitting, onSubmit } = props;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<ExtendShiftFormValues>({
    resolver: zodResolver(extendShiftFormSchema),
    defaultValues: {
      supervisorName: "",
      reason: "",
      additionalDetails: "",
    },
    mode: "onChange",
  });

  const ADDITIONAL_DETAILS_MAX_LENGTH = 200;
  const additionalDetailsLength = watch("additionalDetails")?.length ?? 0;
  const remainingCharacters = ADDITIONAL_DETAILS_MAX_LENGTH - additionalDetailsLength;

  const handleClose = useCallback(() => {
    reset();
    modalState.closeModal();
  }, [modalState, reset]);

  const onFormSubmit = useCallback(() => {
    void handleSubmit((values: ExtendShiftFormValues) => {
      onSubmit(formatShiftExtensionReason(values));
    })();
  }, [handleSubmit, onSubmit]);

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <DialogContent sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}>
        <Stack spacing={5} sx={{ paddingY: 4 }}>
          <Stack spacing={2}>
            <Title semibold variant="h4" component="h1">
              Extend your shift
            </Title>
            <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
              Your workplace will review this form before extra time is approved for payment
            </Text>
          </Stack>

          <FormControl fullWidth>
            <FormLabel>
              <Text semibold variant="body2">
                Your supervisor&apos;s name:
              </Text>
            </FormLabel>
            <Controller
              name="supervisorName"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Input {...field} fullWidth placeholder="First name" />
                  {fieldState.isTouched && isDefined(fieldState.error) && (
                    <FormHelperText error>{fieldState.error.message}</FormHelperText>
                  )}
                </>
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel>
              <Text semibold variant="body2">
                Why are you intending to stay past clock-out time?
              </Text>
            </FormLabel>
            <Controller
              name="reason"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Select
                    {...field}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Text
                            variant="body2"
                            sx={{ color: (theme) => theme.palette.text.disabled }}
                          >
                            Select a reason
                          </Text>
                        );
                      }
                      return selected;
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxWidth: "100%",
                        },
                      },
                    }}
                  >
                    {SHIFT_EXTENSION_REASONS.map((reason) => (
                      <MenuItem
                        key={reason}
                        value={reason}
                        sx={{
                          borderRadius: 0,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        <Text variant="body2">{reason}</Text>
                      </MenuItem>
                    ))}
                  </Select>
                  {isDefined(fieldState.error) && (
                    <FormHelperText error>{fieldState.error.message}</FormHelperText>
                  )}
                </>
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel>
              <Text semibold variant="body2">
                Additional details (optional)
              </Text>
            </FormLabel>
            <Controller
              name="additionalDetails"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <TextArea {...field} rows={4} placeholder="Anything you would like to add..." />
                  <Stack
                    direction="row"
                    justifyContent={isDefined(fieldState.error) ? "space-between" : "flex-end"}
                    sx={{ marginTop: 0.5 }}
                  >
                    {isDefined(fieldState.error) && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                    <Text
                      variant="caption"
                      sx={{
                        color: (theme) =>
                          remainingCharacters < 0
                            ? theme.palette.error.main
                            : theme.palette.text.secondary,
                      }}
                    >
                      {remainingCharacters} characters remaining
                    </Text>
                  </Stack>
                </>
              )}
            />
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogFooter
        withShadow
        orientation="horizontal"
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}
      >
        <IconButton
          variant="outlined"
          iconType="arrow-left"
          size="large"
          aria-label="Go back"
          onClick={handleClose}
        />
        <Button
          sx={{ minWidth: 180 }}
          variant="contained"
          size="large"
          disabled={!isValid || isSubmitting}
          onClick={onFormSubmit}
        >
          Submit
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
